/**
 * One row of the DDI list: the initiative, its two texts, and the status
 * report behind it opened in place when the writer wants to read it.
 *
 * The report is shown as readable text (ReportReader) rather than as a
 * shrunken slide, so reading and writing happen in the same spot. The texts
 * are the same ones the slide draws from: one edit state, one autosave.
 */

import { Link } from 'react-router-dom'
import { AlertTriangle, Check, ChevronDown, ChevronUp, CornerDownLeft, Loader2, Sparkles } from 'lucide-react'
import ReportReader, { filedLine } from './ReportReader'
import AutoGrow, { ddiFieldClass } from './AutoGrow'
import { STATUS_HEX, STATUS_LABEL } from './DdiSlide'
import { formatGoLive, type DdiRow } from '../lib/ddi'
import { monthLabel } from '../lib/report'
import { PROJECT_STAGES } from '../types'
import type { ProjectReport } from '../types'

export interface Texts {
  summary: string
  nextSteps: string
}

export type SaveState = { state: 'saving' | 'saved' } | { state: 'error'; message: string }

export default function DdiRowEditor({
  row, report, month, prevMonth, texts, save, issues, carried, editable, open, redrafting,
  onToggle, onEdit, onBlur, onRetry, onRedraft, onViewSlide,
}: {
  row: DdiRow
  report: ProjectReport | undefined
  month: string
  prevMonth: string
  texts: Texts
  save?: SaveState
  issues: string[]
  carried: Texts | null
  editable: boolean
  /** Whether the status report is open beneath the texts. */
  open: boolean
  redrafting: boolean
  onToggle: () => void
  onEdit: (patch: Partial<Texts>) => void
  onBlur: () => void
  onRetry: () => void
  onRedraft: () => void
  onViewSlide: () => void
}) {
  const stage = PROJECT_STAGES.find(s => s.key === row.stage)?.label
  const prevName = monthLabel(prevMonth).split(' ')[0]
  const flagged = issues.length > 0
  const canEdit = editable && row.hasReport
  const empty = !texts.summary.trim() && !texts.nextSteps.trim()

  return (
    <div data-row-editor={row.projectId} className={flagged ? 'border-l-2 border-l-[#F88806]' : undefined}>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1.5fr)_minmax(0,1fr)] gap-3 lg:gap-4">
        <div className="min-w-0">
          <button type="button" onClick={onToggle} aria-expanded={open}
                  className="group text-left font-semibold text-slate-800 hover:text-[#005EB8] inline-flex items-center gap-1.5 max-w-full">
            <span className="truncate">{row.name}</span>
            {open ? <ChevronUp className="w-3.5 h-3.5 shrink-0 text-[#005EB8]" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0 text-slate-300 group-hover:text-[#005EB8]" />}
          </button>
          <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {row.status && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_HEX[row.status] }} />
                {STATUS_LABEL[row.status]}
              </span>
            )}
            {stage && <span className="text-slate-400">{stage}</span>}
            <span className="text-slate-400">Go live {formatGoLive(row.goLive, row.goLivePrecision)}</span>
          </div>
          <div className={`mt-1 text-xs ${report ? 'text-slate-400' : 'text-[#B8640B] font-semibold'}`}>{filedLine(report)}</div>
          <div className="mt-1 text-xs h-4 flex items-center gap-3">
            {row.hasReport && (
              <button type="button" onClick={onToggle}
                      className={`font-semibold ${open ? 'text-[#005EB8]' : 'text-[#0077A8] hover:text-[#005EB8]'}`}>
                {open ? 'Hide the report' : 'Read the report'}
              </button>
            )}
            {save?.state === 'saving' && <span className="text-slate-400">Saving…</span>}
            {save?.state === 'saved' && <span className="text-slate-400 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
            {save?.state === 'error' && (
              <span className="text-[#B8242E]">
                {save.message} <button onClick={onRetry} className="underline font-semibold">Retry</button>
              </span>
            )}
          </div>
        </div>

        {row.hasReport ? (
          <>
            <label className="block min-w-0">
              <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 lg:hidden">Summary</span>
              <AutoGrow value={texts.summary} onChange={v => onEdit({ summary: v })} onBlur={onBlur}
                        disabled={!canEdit} aria-label={`${row.name} summary`}
                        className={ddiFieldClass(!texts.summary.trim())}
                        placeholder="Where it stands and why, in one or two sentences" />
            </label>
            <label className="block min-w-0">
              <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 lg:hidden">Way forward</span>
              <AutoGrow value={texts.nextSteps} onChange={v => onEdit({ nextSteps: v })} onBlur={onBlur}
                        disabled={!canEdit} aria-label={`${row.name} way forward`}
                        className={ddiFieldClass(!texts.nextSteps.trim(), flagged)}
                        placeholder="One action per line, verb first, with a date where there is one" />
            </label>
            {(flagged || (carried && canEdit && empty)) && (
              <div className="lg:col-start-2 lg:col-span-2 -mt-1 flex flex-wrap items-start gap-x-4 gap-y-1 text-xs">
                {flagged && (
                  <span className="inline-flex items-start gap-1.5 text-[#7A4A00]">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px text-[#F88806]" />
                    Check this draft: {issues.join('. ')}
                  </span>
                )}
                {carried && canEdit && empty && (
                  <button onClick={() => onEdit(carried)}
                          className="text-[#0077A8] font-semibold hover:text-[#005EB8] inline-flex items-center gap-1">
                    <CornerDownLeft className="w-3.5 h-3.5" /> Copy from {prevName}
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="lg:col-span-2 text-sm text-slate-500 self-center">
            No status report filed for {monthLabel(month)}, so there is nothing to summarise yet.{' '}
            <Link to={`/projects/${row.projectId}/report?month=${month}`} className="text-[#0077A8] font-semibold hover:text-[#005EB8]">
              Open the status report
            </Link>
          </div>
        )}
      </div>

      {/* The report, in place, while the writer wants it: a plain band across
          the card, square-edged, so the card keeps its own corners */}
      {open && report && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4 space-y-3">
          <ReportReader report={report} month={month} prevMonth={prevMonth} onViewSlide={onViewSlide} />
          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={onRedraft} disabled={redrafting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#005EB8]/30 bg-white text-xs font-semibold text-[#005EB8] hover:bg-[#005EB8]/5 shadow-sm disabled:opacity-60">
                {redrafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {redrafting ? 'Drafting…' : 'Redraft with AI from this report'}
              </button>
              {carried && (
                <button onClick={() => onEdit(carried)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm">
                  <CornerDownLeft className="w-3.5 h-3.5" /> Copy from {prevName}
                </button>
              )}
            </div>
          )}
          {/* Last month's texts, laid out like the boxes above them */}
          {carried && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-1 bg-slate-50 border-b border-slate-200 text-[10px] font-bold tracking-[0.14em] uppercase text-slate-500">
                On the {prevName} slide
              </div>
              <div className="grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] md:divide-x divide-slate-200">
                <div className="px-4 py-2 min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Summary</div>
                  <p className="mt-0.5 text-sm text-slate-700 leading-snug">{carried.summary || '-'}</p>
                </div>
                <div className="px-4 py-2 min-w-0 border-t md:border-t-0 border-slate-200">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Way forward</div>
                  <p className="mt-0.5 text-sm text-slate-700 leading-snug whitespace-pre-line">{carried.nextSteps || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
